// Allow for async generators to be used in this file without requiring them to have a yield
// eslint-disable-file require-yield

import axios from 'axios';
import { action, observable, runInAction, makeObservable } from 'mobx';
import { NavigateFunction, Location } from 'react-router-dom';
import * as uuid from 'uuid';

import { SERVER_URL } from 'Constants';
import {
	AdditionalBookingOptionEntity,
	AdditionalBookingSectionEntity,
	CargoCharacteristicEntity,
	CheckInStatusEntity,
	TowOnTypeEntity,
} from 'Models/Entities';
import { IFerryTripEntityAttributes } from 'Models/Entities/FerryTripEntity';
import { Model } from 'Models/Model';
import { store } from 'Models/Store';
import alertToast from 'Util/ToastifyUtils';
import { isNotNullOrUndefined, isNullOrUndefined } from 'Util/TypeGuards';
import { CheckInBookingOverviewDto } from '../CheckInEntities/CheckInBookingOverviewDto';
import { FerryTripDto } from '../CheckInEntities/FerryTripDto';
import { ReturnToCheckInPageModal } from '../Helpers/OfflineModals';
import { CapacityTypeForCheckInDto } from '../Types/CapacityTypeForCheckInDto';
import { CargoTypeForCheckInDto } from '../Types/CargoTypeForCheckInDto';
import { CheckInStore } from './CheckInContext';

const DATABASE_NAME = 'offline-storage';
/**
 * Increment this when the database schema changes
 */
const DATABASE_VERSION = 3;

const HOUR = 60 * 60 * 1000; // 1 hour (in milliseconds)

// How often to re-test the connection while the browser reports being offline (see verifyConnectivity)
const CONNECTIVITY_RECHECK_INTERVAL = 30 * 1000; // 30 seconds (in milliseconds)

// Expiry time after the ferry trip has arrived at its destination
const TRIP_EXPIRY_TIME = HOUR; // 1 hour (in milliseconds)

// Expiry time after the cache has been created (and the ferry trip expiry has passed)
const CACHE_EXPIRY_TIME = 4 * HOUR; // 4 hours (in milliseconds)

const enum DB_TABLES {
	ferryTrips = 'ferryTrips',
	bookings = 'bookings',
	capacityTypes = 'capacityTypes',
	cargoCharacteristics = 'cargoCharacteristics',
	cargoTypes = 'cargoTypes',
	towTypes = 'towTypes',
	additionalBookingSections = 'additionalBookingSections',
	additionalBookOptions = 'additionalBookOptions',
	bookingStatusChanges = 'bookingStatusChanges',
	checkInStatuses = 'checkInStatuses',
}

const getAllTables = () => {
	return [
		DB_TABLES.ferryTrips,
		DB_TABLES.bookings,
		DB_TABLES.capacityTypes,
		DB_TABLES.cargoCharacteristics,
		DB_TABLES.cargoTypes,
		DB_TABLES.towTypes,
		DB_TABLES.additionalBookingSections,
		DB_TABLES.additionalBookOptions,
		DB_TABLES.bookingStatusChanges,
		DB_TABLES.checkInStatuses,
	];
};

const addTable = (
	db: IDBDatabase,
	table: DB_TABLES,
	keyPath: string = 'id',
	additionalChanges?: (objectStore: IDBObjectStore) => void,
) => {
	if (!db.objectStoreNames.contains(table)) {
		const objectStore = db.createObjectStore(table, { keyPath });

		if (additionalChanges) {
			additionalChanges(objectStore);
		}
	}
};

const handleDbRequest = <T,>(request: IDBRequest<T>): Promise<T> => {
	return new Promise((resolve, reject) => {
		request.onsuccess = () => {
			resolve(request.result);
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
};

/**
 * Create a transaction and executes each operation in parallel (it does not wait for each operation to finish before
 * executing the next one). This is useful for read operations but may not be suitable for write operations.
 *
 * @param db to perform the transaction on
 * @param tables that will be accessed by the transaction
 * @param transactionMode whether the transaction is read-only or read-write
 * @param operations an async function that executes the operations in the transaction. The function should not include
 *                      any await statements
 */
const createTransactionParallel = async (
	db: IDBDatabase,
	tables: DB_TABLES[],
	transactionMode: IDBTransactionMode = 'readonly',
	operations: (t: IDBTransaction) => AsyncGenerator<IDBRequest<any>>,
) => {
	const transaction = db.transaction(tables, transactionMode);
	const promises: Promise<any>[] = [];

	try {
		for await (const op of operations(transaction)) {
			promises.push(handleDbRequest(op));
		}

		await Promise.all(promises);

		transaction.commit();
	} catch (err) {
		transaction.abort();

		throw err;
	}
};

/**
 * Create a transaction and executes each operation sequentially. This should be more reliable than running the operations
 * in parallel but may be slower. (Use this if you are unsure)
 *
 * @param db to perform the transaction on
 * @param tables that will be accessed by the transaction
 * @param transactionMode whether the transaction is read-only or read-write
 * @param operations an async function that executes the operations in the transaction. The function should not include
 *                      any await statements
 */
const createTransaction = async <T,>(
	db: IDBDatabase,
	tables: DB_TABLES[],
	transactionMode: IDBTransactionMode = 'readonly',
	operations: (t: IDBTransaction) => AsyncGenerator<IDBRequest<any>, T, any>,
): Promise<T> => {
	const transaction = db.transaction(tables, transactionMode);

	try {
		const generator = operations(transaction);
		let result = await generator.next();

		while (!result.done) {
			// eslint-disable-next-line no-await-in-loop
			const value = await handleDbRequest(result.value);

			// eslint-disable-next-line no-await-in-loop
			result = await generator.next(value);
		}

		transaction.commit();

		return await result.value;
	} catch (err) {
		transaction.abort();

		throw err;
	}
};

const toDbObject = (x: any) => (x instanceof Model ? x.getObject() : Model.getObject(x));

const fromDbObject = <T,>(x: any, Type: { new (a: Partial<T>): T }) => new Type(x);

interface BookingStatusChange {
	id: string;
	checkInStatusId?: string;
	bookingId: string;
	ferryTripId: string;
	stopId?: string;
	userId?: string;
	created: number;
	/**
	 * Per-device counter, so two changes made in the same millisecond still replay in the order they were made.
	 * Changes queued before it existed have none, and sort as 0.
	 */
	sequence?: number;
}

interface SyncCheckInDataResult {
	processedIds: string[];
	failed: { id: string; reason: string }[];
}

const SEQUENCE_STORAGE_KEY = 'checkInOfflineSequence';
let lastSequence = 0;

/**
 * The next value of the per-device counter. Kept in localStorage so it keeps rising across reloads; when storage is
 * unavailable it still rises for as long as the page is open, which is all the ordering needs within a session.
 */
const nextSequence = (): number => {
	let stored = 0;
	try {
		stored = Number(window.localStorage.getItem(SEQUENCE_STORAGE_KEY)) || 0;
	} catch {
		// Storage blocked: fall back to the in-memory counter
	}

	lastSequence = Math.max(lastSequence, stored) + 1;

	try {
		window.localStorage.setItem(SEQUENCE_STORAGE_KEY, String(lastSequence));
	} catch {
		// Storage blocked: the in-memory counter still rises
	}

	return lastSequence;
};

export default class CheckInCache {
	private static instance?: CheckInCache;

	@observable
	public isOnline: boolean = true;

	@observable
	public isConnected: boolean = false;

	@observable
	public isConnecting: boolean = false;

	private database: IDBDatabase;

	@observable
	private ferryTripIds: string[] | undefined;

	/** In-flight connectivity probe, so concurrent callers share the one request. */
	private connectivityProbe?: Promise<boolean>;

	private connectivityRecheckTimer?: ReturnType<typeof setTimeout>;

	private constructor() {
		makeObservable(this);

		// Get the initial state of the browser's connection status
		this.isOnline = navigator.onLine;

		window.addEventListener('online', this.handleOnlineChange);
		window.addEventListener('offline', this.handleOfflineChange);

		// Don't take the browser's word for it — see verifyConnectivity.
		if (!this.isOnline) {
			this.verifyConnectivity();
		}
	}

	@action
	public handleOnlineChange = (): void => {
		this.isOnline = true;
		this.stopConnectivityRechecks();
	};

	@action
	public handleOfflineChange = (): void => {
		this.isOnline = false;
		this.verifyConnectivity();
	};

	/**
	 * Confirms whether the server is actually reachable and updates {@link isOnline} to match.
	 *
	 * `navigator.onLine === false` only means the browser *believes* there is no network. VPNs,
	 * virtual adapters and a wedged network-change notifier all leave it stuck on false while
	 * requests work perfectly, which used to drop check-in into offline mode (cached data, hidden
	 * actions, offline banner) on a fully connected device. Reaching the server is the only thing we
	 * actually care about, so ask it directly. Any HTTP response proves connectivity — only a
	 * network-level failure rejects — so the status code is irrelevant.
	 *
	 * While the browser keeps claiming to be offline we re-check periodically, so a genuine
	 * disconnect still flips us into offline mode even though no further `offline` event will fire.
	 */
	public verifyConnectivity = (): Promise<boolean> => {
		if (this.connectivityProbe) {
			return this.connectivityProbe;
		}

		this.connectivityProbe = (async () => {
			let reachable: boolean;
			try {
				await fetch(`${SERVER_URL}/api/alive`, { method: 'HEAD', cache: 'no-store' });
				reachable = true;
			} catch {
				reachable = false;
			}

			runInAction(() => {
				this.isOnline = reachable;
			});

			this.connectivityProbe = undefined;

			// Keep watching only while the browser itself still reports offline. Once it reports
			// online again the `online` event takes over and the rechecks stop.
			if (!navigator.onLine) {
				this.scheduleConnectivityRecheck();
			}

			return reachable;
		})();

		return this.connectivityProbe;
	};

	private scheduleConnectivityRecheck = (): void => {
		this.stopConnectivityRechecks();
		this.connectivityRecheckTimer = setTimeout(this.verifyConnectivity, CONNECTIVITY_RECHECK_INTERVAL);
	};

	private stopConnectivityRechecks = (): void => {
		if (this.connectivityRecheckTimer !== undefined) {
			clearTimeout(this.connectivityRecheckTimer);
			this.connectivityRecheckTimer = undefined;
		}
	};

	@action
	public connect = (): Promise<void> => {
		if (this.isConnected) {
			return Promise.resolve();
		}

		return new Promise((resolve, reject) => {
			this.isConnecting = true;

			const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

			request.onupgradeneeded = (e: IDBVersionChangeEvent) => {
				const db = (e.target as IDBOpenDBRequest).result;
				this.updateSchema(db, e.oldVersion);
			};

			request.onerror = e => reject(e);

			request.onsuccess = e => {
				this.database = (e.target as IDBOpenDBRequest).result;
				this.isConnected = true;
				this.isConnecting = false;
				resolve();
			};
		});
	};

	/**
	 * Store the data locally in the cache
	 *
	 * @param checkInStore the store holding the data to be cached
	 */
	@action
	public cacheData = async (checkInStore: CheckInStore) => {
		if (!this.isConnected) {
			throw new Error('Database is not connected');
		}
		const towOnTypes = await checkInStore.getAllTowOnTypes();
		const additionalBookingSections = await checkInStore.getAllAdditionalBookingSections();
		const cargoTypes = await checkInStore.getAllVehicleTypes();

		await createTransaction(this.database, getAllTables(), 'readwrite', async function* (transaction) {
			// For now we only support one ferry trip in the cache
			const ferryTrips = await handleDbRequest(transaction.objectStore(DB_TABLES.ferryTrips).getAll());
			if (ferryTrips.length > 0) {
				throw new Error('There is already ferry trips in the cache');
			}

			// Add the ferry trip
			const ferryTripStore = transaction.objectStore(DB_TABLES.ferryTrips);
			const ferryTrip = toDbObject(checkInStore.ferryTrip);
			// Update the created date for the ferry trip so it can be determined when the cache was created
			ferryTrip.created = Date.now();
			yield ferryTripStore.add(ferryTrip);

			// Add all the bookings
			const bookingStore = transaction.objectStore(DB_TABLES.bookings);
			for (const booking of checkInStore.bookings) {
				yield bookingStore.add(toDbObject(booking));
			}

			const capacityTypeStore = transaction.objectStore(DB_TABLES.capacityTypes);
			for (const capacityType of checkInStore.capacityTypes) {
				yield capacityTypeStore.add(toDbObject(capacityType));
			}

			const cargoCharacteristicStore = transaction.objectStore(DB_TABLES.cargoCharacteristics);
			for (const cargoCharacteristic of checkInStore.cargoCharacteristics) {
				yield cargoCharacteristicStore.add(toDbObject(cargoCharacteristic));
			}

			// Add all cargo types
			const cargoTypeStore = transaction.objectStore(DB_TABLES.cargoTypes);
			for (const cargoType of cargoTypes) {
				yield cargoTypeStore.add(toDbObject(cargoType));
			}

			// Add all tow on types
			const towOnTypeStore = transaction.objectStore(DB_TABLES.towTypes);
			for (const towOnType of towOnTypes) {
				yield towOnTypeStore.add(toDbObject(towOnType));
			}

			// Add all additional booking options and sections
			const additionalBookingSectionStore = transaction.objectStore(DB_TABLES.additionalBookingSections);
			for (const additionalBookingSection of additionalBookingSections) {
				yield additionalBookingSectionStore.add(toDbObject(additionalBookingSection));
			}

			if (!!checkInStore.allAdditionalBookingOptions) {
				const additionalBookingOptionStore = transaction.objectStore(DB_TABLES.additionalBookOptions);
				for (const additionalBookingOption of checkInStore.allAdditionalBookingOptions) {
					yield additionalBookingOptionStore.add(toDbObject(additionalBookingOption));
				}
			}

			const checkInStatusStore = transaction.objectStore(DB_TABLES.checkInStatuses);
			for (const checkInStatus of Object.values(checkInStore.checkInStatuses)) {
				for (const status of checkInStatus) {
					yield checkInStatusStore.add(toDbObject(status));
				}
			}
		});

		// Clear the ferry trip ids
		this.ferryTripIds = undefined;
	};

	/**
	 * Load the data from the cache into the store
	 *
	 * @param checkInStore
	 */
	@action
	public loadCachedData = async (checkInStore: CheckInStore) => {
		if (!this.isConnected) {
			throw new Error('Database is not connected');
		}

		let ferryTrip: FerryTripDto | undefined;
		let bookings: CheckInBookingOverviewDto[];
		let capacityTypes: CapacityTypeForCheckInDto[];
		let cargoCharacteristics: CargoCharacteristicEntity[];
		let vehicleTypes: CargoTypeForCheckInDto[];
		let towOnTypes: TowOnTypeEntity[];
		let additionalBookingSections: AdditionalBookingSectionEntity[];
		let additionalBookingOptions: AdditionalBookingOptionEntity[];
		let checkInStatuses: CheckInStatusEntity[];

		await createTransaction(this.database, getAllTables(), 'readonly', async function* (transaction) {
			const ferryTripStore = transaction.objectStore(DB_TABLES.ferryTrips);
			const ferryTrips = yield ferryTripStore.getAll();
			// An empty cache must leave the trip undefined. `new FerryTripDto(undefined)` is truthy but
			// has no id/routeId, and the check-in page treats any truthy trip as loaded.
			ferryTrip = ferryTrips[0] === undefined ? undefined : fromDbObject(ferryTrips[0], FerryTripDto);

			const bookingStore = transaction.objectStore(DB_TABLES.bookings);
			bookings = (yield bookingStore.getAll()).map((x: any) => fromDbObject(x, CheckInBookingOverviewDto));

			const capacityTypeStore = transaction.objectStore(DB_TABLES.capacityTypes);
			capacityTypes = (yield capacityTypeStore.getAll()).map((x: any) =>
				fromDbObject(x, CapacityTypeForCheckInDto),
			);

			const cargoCharacteristicStore = transaction.objectStore(DB_TABLES.cargoCharacteristics);
			cargoCharacteristics = (yield cargoCharacteristicStore.getAll()).map((x: any) =>
				fromDbObject(x, CargoCharacteristicEntity),
			);

			const cargoTypeStore = transaction.objectStore(DB_TABLES.cargoTypes);
			vehicleTypes = (yield cargoTypeStore.getAll()).map((x: any) => fromDbObject(x, CargoTypeForCheckInDto));

			const towOnTypeStore = transaction.objectStore(DB_TABLES.towTypes);
			towOnTypes = (yield towOnTypeStore.getAll()).map((x: any) => fromDbObject(x, TowOnTypeEntity));

			const additionalBookingSectionStore = transaction.objectStore(DB_TABLES.additionalBookingSections);
			additionalBookingSections = (yield additionalBookingSectionStore.getAll()).map((x: any) =>
				fromDbObject(x, AdditionalBookingSectionEntity),
			);

			const additionalBookingOptionStore = transaction.objectStore(DB_TABLES.additionalBookOptions);
			additionalBookingOptions = (yield additionalBookingOptionStore.getAll()).map((x: any) =>
				fromDbObject(x, AdditionalBookingOptionEntity),
			);

			const checkInStatusStore = transaction.objectStore(DB_TABLES.checkInStatuses);
			checkInStatuses = (yield checkInStatusStore.getAll()).map((x: any) => fromDbObject(x, CheckInStatusEntity));
		});

		runInAction(() => {
			if (!!ferryTrip) {
				checkInStore.ferryTrip = ferryTrip;
			}

			if (!!bookings) {
				checkInStore.bookings = bookings;
			}

			if (!!capacityTypes) {
				checkInStore.capacityTypes = capacityTypes;
			}

			if (!!cargoCharacteristics) {
				checkInStore.cargoCharacteristics = cargoCharacteristics;
			}

			if (!!vehicleTypes) {
				checkInStore.allVehicleTypes = vehicleTypes;
				checkInStore.vehicleTypeMap = new Map(vehicleTypes.map(x => [x.id, x]));
			}

			if (!!towOnTypes) {
				checkInStore.allTowOnTypes = towOnTypes;
			}

			if (!!additionalBookingOptions) {
				checkInStore.allAdditionalBookingOptions = additionalBookingOptions;
			}

			if (!!additionalBookingSections) {
				checkInStore.allAdditionalBookingSections = additionalBookingSections;
			}

			if (!!checkInStatuses) {
				checkInStore.checkInStatuses = checkInStatuses.reduce(
					(acc: { [key: string]: CheckInStatusEntity[] }, status: CheckInStatusEntity) => {
						const key = status.bookingType;

						if (!acc[key]) {
							acc[key] = [];
						}

						acc[key].push(status);
						return acc;
					},
					{},
				);
			}
		});
	};

	/**
	 * Clear the data from the cache. Except for any booking status changes
	 *
	 * For now, we only support one ferry trip in the cache, this method will need to be updated when we support multiple
	 */
	@action
	public clearCachedData = async () => {
		if (!this.isConnected) {
			throw new Error('Database is not connected');
		}

		await createTransaction(this.database, getAllTables(), 'readwrite', async function* (transaction) {
			const ferryTripStore = transaction.objectStore(DB_TABLES.ferryTrips);
			yield ferryTripStore.clear();

			const bookingStore = transaction.objectStore(DB_TABLES.bookings);
			yield bookingStore.clear();

			const capacityTypeStore = transaction.objectStore(DB_TABLES.capacityTypes);
			yield capacityTypeStore.clear();

			const cargoCharacteristicStore = transaction.objectStore(DB_TABLES.cargoCharacteristics);
			yield cargoCharacteristicStore.clear();

			const cargoTypeStore = transaction.objectStore(DB_TABLES.cargoTypes);
			yield cargoTypeStore.clear();

			const towOnTypeStore = transaction.objectStore(DB_TABLES.towTypes);
			yield towOnTypeStore.clear();

			const additionalBookingSectionStore = transaction.objectStore(DB_TABLES.additionalBookingSections);
			yield additionalBookingSectionStore.clear();

			const additionalBookingOptionStore = transaction.objectStore(DB_TABLES.additionalBookOptions);
			yield additionalBookingOptionStore.clear();

			const checkInStatusStore = transaction.objectStore(DB_TABLES.checkInStatuses);
			yield checkInStatusStore.clear();
		});

		this.ferryTripIds = undefined;
	};

	@action
	public syncChangedData = async (ferryTripId: string) => {
		const bookingStatusChanges: BookingStatusChange[] = await createTransaction(
			this.database,
			[DB_TABLES.bookingStatusChanges],
			'readonly',
			async function* (transaction) {
				const bookingStatusChangeStore = transaction.objectStore(DB_TABLES.bookingStatusChanges);
				return yield bookingStatusChangeStore.getAll();
			},
		);

		if (bookingStatusChanges.length === 0) {
			return;
		}

		// In the order they were made. The server sorts them too; this keeps the request readable.
		const orderedChanges = [...bookingStatusChanges].sort(
			(a, b) => a.created - b.created || (a.sequence ?? 0) - (b.sequence ?? 0),
		);

		let result: SyncCheckInDataResult;
		try {
			const response = await axios.post<SyncCheckInDataResult>('/api/check-in/sync-check-in-data', {
				checkInData: orderedChanges.map(x => {
					const occurredAt = new Date(x.created).toISOString();
					return {
						...x,
						// Null is a check-out: the status was cleared back to Booked
						checkInStatusId: x.checkInStatusId ?? null,
						created: occurredAt,
						occurredAt,
						sequence: x.sequence ?? 0,
					};
				}),
			});
			result = response.data;
		} catch (err) {
			alertToast('There was an error syncing the check in data', 'error');
			console.error(err);

			return; // Do not delete the booking changes from the cache
		}

		// Delete only what the server saved (now or in an earlier sync). The rest stays queued to try again.
		const processedIds = result?.processedIds ?? [];
		if (processedIds.length !== 0) {
			await createTransaction(
				this.database,
				[DB_TABLES.bookingStatusChanges],
				'readwrite',
				async function* (transaction) {
					const bookingStatusChangeStore = transaction.objectStore(DB_TABLES.bookingStatusChanges);
					for (const id of processedIds) {
						yield bookingStatusChangeStore.delete(id);
					}
				},
			);
		}

		const failed = result?.failed ?? [];
		if (failed.length !== 0) {
			const reasons = [...new Set(failed.map(x => x.reason).filter(x => !!x))].join(' ');
			alertToast(
				`${failed.length} offline check-in ${failed.length === 1 ? 'change was' : 'changes were'} not synced. ${reasons}`,
				'error',
			);
		}
	};

	/**
	 * Update the booking in the cache.
	 *
	 * This is used when the user is still online and they have updated the booking (or received a booking update via
	 * sockets) and we need to update the cache with the new data.
	 *
	 * @param booking
	 */
	@action
	public updateBooking = async (booking: CheckInBookingOverviewDto) => {
		const ferryTripId = booking.bookedSummary?.ferryTrip?.id;
		if (!ferryTripId) {
			throw new Error('Booking does not have a ferry trip');
		}

		const ferryTripIsCached = await this.isFerryTripCached(ferryTripId);
		if (!ferryTripIsCached) {
			throw new Error('Ferry trip is not cached');
		}

		await createTransaction(this.database, [DB_TABLES.bookings], 'readwrite', async function* (transaction) {
			const bookingStore = transaction.objectStore(DB_TABLES.bookings);
			yield bookingStore.put(toDbObject(booking));
		});
	};

	/**
	 * Set a local change to the booking check in status. In the future this
	 * @param bookingId the id of the booking to update in the cache
	 * @param ferryTripId the id of the ferry trip that the status change belongs to
	 * @param newStatusId the id of the new check in status (if null, the booking will have no status assigned to it)
	 * @param stopId the id of the stop that the booking status was changed at
	 * @param userId the id of the user that made the change
	 */
	@action
	public setBookingCheckInStatus = async (
		bookingId: string,
		ferryTripId: string,
		newStatusId: string | null,
		stopId: string | null,
		userId?: string,
	) => {
		await createTransaction(
			this.database,
			[DB_TABLES.bookingStatusChanges, DB_TABLES.bookings],
			'readwrite',
			async function* (transaction) {
				const bookingStore = transaction.objectStore(DB_TABLES.bookings);
				const booking = yield bookingStore.get(bookingId);

				// Ignore setting the check in status if the booking is not found
				if (booking === undefined) {
					return;
				}

				const bookingChangeStore = transaction.objectStore(DB_TABLES.bookingStatusChanges);
				const bookingChange: BookingStatusChange = {
					id: uuid.v4(),
					bookingId,
					ferryTripId,
					stopId: stopId ?? undefined,
					checkInStatusId: newStatusId ?? undefined,
					userId: userId,
					created: Date.now(),
					sequence: nextSequence(),
				};

				yield bookingChangeStore.add(bookingChange);
			},
		);
	};

	@action
	public hasCheckInChanges = async (): Promise<boolean> => {
		if (!this.isConnected) {
			await this.connect();
		}

		return createTransaction(
			this.database,
			[DB_TABLES.bookingStatusChanges],
			'readonly',
			async function* (transaction) {
				const bookingStatusChangeStore = transaction.objectStore(DB_TABLES.bookingStatusChanges);
				const bookingStatusChanges = yield bookingStatusChangeStore.getAll();

				return bookingStatusChanges.length !== 0;
			},
		);
	};

	@action
	public dispose(): void {
		window.removeEventListener('online', this.handleOnlineChange);
		window.removeEventListener('offline', this.handleOfflineChange);
		this.stopConnectivityRechecks();

		// Disconnect from the database
		this.database.close();
		this.isConnected = false;

		CheckInCache.instance = undefined;
	}

	/**
	 * If the page has been reloaded, the user may not be on the correct page
	 */
	public handlePageReload = async (location: Location, navigate: NavigateFunction): Promise<boolean> => {
		const { isStaff } = store;
		if (!isStaff) {
			return false;
		}

		// Always check if we can evict data from the cache
		await this.checkForCacheEviction();

		if (this.isOnline) {
			return false;
		}

		const hasCheckInCached = await this.hasCheckInCached();
		if (!hasCheckInCached) {
			return false;
		}

		// If the user is already on the check in page, do nothing
		const checkInUrl = await this.getCheckInPageForCache();
		const currentUrl = location.pathname;
		if (currentUrl.includes(checkInUrl)) {
			return false;
		}

		if (await ReturnToCheckInPageModal()) {
			navigate(checkInUrl);
			return true;
		}

		return false;
	};

	/**
	 * Redirect the user to the correct page for the check in page
	 */
	public getCheckInPageForCache = async (): Promise<string> => {
		if (!this.isConnected) {
			await this.connect();
		}

		const transaction = this.database.transaction(getAllTables(), 'readonly');

		const ferryTripStore = transaction.objectStore(DB_TABLES.ferryTrips);
		const ferryTrips = await handleDbRequest(ferryTripStore.getAll());
		const ferryTripId = ferryTrips[0].id;

		return `${SERVER_URL}/check-in/${ferryTripId}`;
	};

	public hasCheckInCached = async (): Promise<boolean> => {
		return createTransaction(this.database, [DB_TABLES.ferryTrips], 'readonly', async function* (transaction) {
			const ferryTripStore = transaction.objectStore(DB_TABLES.ferryTrips);
			const ferryTrips = yield ferryTripStore.getAll();

			return ferryTrips.length !== 0;
		});
	};

	/**
	 * Check if any data in the cach has expired and can be removed
	 */
	@action
	public checkForCacheEviction = async () => {
		// Do nothing if we are offline
		if (!this.isOnline) {
			return;
		}

		await this.connect();

		const expiredFerryTripIds = await createTransaction(
			this.database,
			getAllTables(),
			'readonly',
			async function* (transaction) {
				const ferryTripStore = transaction.objectStore(DB_TABLES.ferryTrips);
				const bookingUpdateStore = transaction.objectStore(DB_TABLES.bookingStatusChanges);

				const ferryTrips: IFerryTripEntityAttributes[] = yield ferryTripStore.getAll();
				const ferryTripIds = await Promise.all(
					ferryTrips.map(async ferryTrip => {
						// Do nothing if the ferry trip is still departing
						const now = Date.now();

						// Only clear the ferry trip if:
						// 1. It has been more than an hour since the ferry trip departed
						// 2. It has been more than an hour since the cache for the ferry trip was created
						const hasNotArrived =
							ferryTrip.arrivalDateTime != null && +ferryTrip.arrivalDateTime + TRIP_EXPIRY_TIME > now;
						const cachedRecently =
							ferryTrip.created != null && +ferryTrip.created + CACHE_EXPIRY_TIME > now;
						if (hasNotArrived || cachedRecently) {
							return;
						}

						// Check if there are any changes to the ferry trip
						const bookingsUpdated = await handleDbRequest(
							bookingUpdateStore.index('ferryTripId').count(ferryTrip.id),
						);
						if (bookingsUpdated > 0) {
							return;
						}

						return ferryTrip.id;
					}),
				);

				return ferryTripIds.filter(x => isNotNullOrUndefined(x));
			},
		);

		// If any ferry trips were returned from above, clear the cache (in the future we will need to delete specific
		// ferry trips)
		if (expiredFerryTripIds.length !== 0) {
			await this.clearCachedData();
		}
	};

	@action
	public isFerryTripCached = async (ferryTripId?: string): Promise<boolean> => {
		if (this.ferryTripIds === undefined) {
			this.ferryTripIds = await createTransaction<string[]>(
				this.database,
				[DB_TABLES.ferryTrips],
				'readonly',
				async function* (transaction) {
					const ferryTripStore = transaction.objectStore(DB_TABLES.ferryTrips);
					const ferryTrips: IFerryTripEntityAttributes[] = yield ferryTripStore.getAll();

					return ferryTrips.filter(x => isNotNullOrUndefined(x.id)).map(x => x.id!);
				},
			);
		}

		if (isNullOrUndefined(ferryTripId)) {
			return this.ferryTripIds.length !== 0;
		}

		return this.ferryTripIds.find(x => x === ferryTripId) !== undefined;
	};

	public static getInstance(connect: boolean = true): CheckInCache {
		if (!CheckInCache.instance) {
			CheckInCache.instance = new CheckInCache();

			if (connect) {
				CheckInCache.instance.connect();
			}
		}
		return CheckInCache.instance;
	}

	/**
	 * Update the database schema
	 *
	 * @param db the connection to the database
	 * @param oldVersion version of the database before the update. If we need to rebuild particular tables based on the
	 * previous version, we can do that here (for example if we need to add a new index to a table)
	 */
	@action
	private updateSchema = (db: IDBDatabase, oldVersion: number) => {
		addTable(db, DB_TABLES.ferryTrips);
		addTable(db, DB_TABLES.bookings, 'id', s => {
			s.createIndex('ferryTripId', 'bookedSummary.ferryTrip.id', { unique: false });
		});
		addTable(db, DB_TABLES.capacityTypes);
		addTable(db, DB_TABLES.cargoCharacteristics);
		addTable(db, DB_TABLES.cargoTypes);
		addTable(db, DB_TABLES.towTypes);
		addTable(db, DB_TABLES.checkInStatuses);
		addTable(db, DB_TABLES.additionalBookingSections);
		addTable(db, DB_TABLES.additionalBookOptions);
		addTable(db, DB_TABLES.bookingStatusChanges, 'id', s => {
			s.createIndex('bookingId', 'bookingId', { unique: false });
			s.createIndex('ferryTripId', 'ferryTripId', { unique: false });
			s.createIndex('checkInStatusId', 'checkInStatusId', { unique: false });
		});
	};
}
